import clsx from 'clsx'
import React, { createContext, useCallback, useContext } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import Badge from '@/components-latest/primitives/Badge'
import { textVariants } from '@/components-latest/primitives/Text'

import Styles from './Tabs.module.scss'

const TabsContext = createContext({
  activeTabId: '',
  onTabClick: () => {},
  disabled: false,
  asNavigation: false,
  tabClassName: Styles.tab
} as {
  activeTabId?: string
  onTabClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  disabled?: boolean
  asNavigation?: boolean
  tabClassName?: string
})

type ITabsProps = {
  className?: string
  activeTabId?: string
  onTabChange?: (tabId: string) => void
  disabled?: boolean
  children?: React.ReactNode
  asNavigation?: boolean
  wrapperClassName?: string
  tabClassName?: string
}

const Tabs = React.memo(
  ({
    className,
    activeTabId,
    onTabChange = () => {},
    disabled,
    children,
    asNavigation,
    wrapperClassName,
    tabClassName
  }: ITabsProps) => {
    const onTabClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.currentTarget
        const tabId = target.dataset.tabId
        if (tabId) {
          onTabChange(tabId)
        }
      },
      [onTabChange]
    )

    return (
      <TabsContext.Provider
        value={{
          activeTabId,
          onTabClick,
          disabled,
          asNavigation,
          tabClassName: tabClassName ?? Styles.tab
        }}>
        <div
          data-testid="tabs"
          className={clsx(Styles.wrapper, wrapperClassName, className)}>
          {children}
        </div>
      </TabsContext.Provider>
    )
  }
)

Tabs.displayName = 'Tabs'

type ITabProps = {
  label?: string
  id: string
  disabled?: boolean
  className?: string
  children?: React.ReactNode
  counter?: number
}

const Tab = React.memo(
  ({ id, label, className, disabled, children, counter }: ITabProps) => {
    const {
      disabled: tabsWrapperDisabled,
      activeTabId,
      onTabClick,
      asNavigation,
      tabClassName
    } = useContext(TabsContext)
    
    const location = useLocation()
    const isActive = asNavigation ? location.pathname === id : String(id) === activeTabId

    const isSimpleString = label || typeof children === 'string'

    const tabClasses = clsx(
      tabClassName,
      textVariants({ variant: 'nextBody' }),
      className,
      {
        [Styles.active]: isActive,
        [textVariants({ variant: 'nextBody', weight: 'bold' })]: isActive,
        [Styles.disabled]: disabled || tabsWrapperDisabled,
        [Styles.withCounter]: counter !== undefined
      }
    )

    return (
      <>
        {asNavigation ? (
          <NavLink
            to={id}
            key={id}
            className={tabClasses}
            activeClassName={clsx(
              Styles.active,
              textVariants({ variant: 'nextBody', weight: 'bold' })
            )}>
            {isSimpleString && (
              <span
                className={clsx(
                  Styles.spaceHolder,
                  textVariants({ variant: 'nextBody', weight: 'bold' })
                )}>
                {label || children || ''}
              </span>
            )}
            <span className={Styles.tabChildren}>
              {label || children || ''}
            </span>
            {counter !== undefined ? (
              <Badge type="number" size="small">
                {counter}
              </Badge>
            ) : null}
          </NavLink>
        ) : (
          <div
            onClick={tabsWrapperDisabled || disabled ? undefined : onTabClick}
            key={id}
            data-tab-id={id}
            data-active={String(id) === activeTabId}
            className={tabClasses}>
            {label || children || ''}
          </div>
        )}
      </>
    )
  }
)

Tab.displayName = 'Tab'

export { Tab }
export type { ITabsProps }
export default Tabs
